/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  SafeAreaView,
  StatusBar,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Linking,
  Share,
  Dimensions,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const ENROLLED_KEY = 'live_enrolled_ids';
const REMINDER_KEY = 'live_reminder_ids';
const FAVORITE_KEY = 'live_favorite_ids';

// Render-safe date formatter. course.date arrives as a JS Date via navigation
// params (see LiveVideoCoursesScreen); a raw Date can't be rendered as a React
// child, so always convert it to a string before putting it in <Text>.
const fmtDate = (d) => {
  if (!d) return 'TBA';
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return 'TBA';
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

// Safe hostname extraction (the platform label) without crashing on bad URLs.
const hostnameOf = (url) => {
  if (!url) return 'Recording';
  try { return new URL(url).hostname; } catch {}
  const m = String(url).match(/^https?:\/\/([^/]+)/i);
  return m ? m[1] : 'Online';
};

const addId = async (key, id) => {
  try {
    const raw = await AsyncStorage.getItem(key);
    const list = raw ? JSON.parse(raw) : [];
    const next = Array.from(new Set([...list, id]));
    await AsyncStorage.setItem(key, JSON.stringify(next));
  } catch {}
};
const removeId = async (key, id) => {
  try {
    const raw = await AsyncStorage.getItem(key);
    const list = raw ? JSON.parse(raw) : [];
    await AsyncStorage.setItem(key, JSON.stringify(list.filter(x => x !== id)));
  } catch {}
};
const hasId = async (key, id) => {
  try {
    const raw = await AsyncStorage.getItem(key);
    const list = raw ? JSON.parse(raw) : [];
    return list.includes(id);
  } catch { return false; }
};

const LiveCourseDetailsScreen = ({ route, navigation }) => {
  const { course } = route.params;
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isReminderSet, setIsReminderSet] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Load persisted state for this course.
  useEffect(() => {
    (async () => {
      setIsEnrolled(await hasId(ENROLLED_KEY, course.id));
      setIsReminderSet(await hasId(REMINDER_KEY, course.id));
      setIsFavorite(await hasId(FAVORITE_KEY, course.id));
    })();
  }, [course.id]);

  // After a successful payment, PaymentScreen routes back here with justEnrolled.
  useEffect(() => {
    if (route.params?.justEnrolled) {
      setIsEnrolled(true);
      addId(ENROLLED_KEY, course.id);
      recordEnrollment(); // best-effort server sync
    }
  }, [route.params?.justEnrolled]);

  // Best-effort backend record of an enrollment (non-blocking).
  const recordEnrollment = useCallback(async () => {
    try {
      const token = (await AsyncStorage.getItem('auth_token')) || (await AsyncStorage.getItem('userToken')) || '';
      const userId = (await AsyncStorage.getItem('user_id')) || (await AsyncStorage.getItem('userId')) || null;
      await fetch(`${API_BASE}/api/data/live_enrollments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          course_id: course.id,
          course_title: course.title,
          user_id: userId,
          status: 'enrolled',
          enrolled_at: new Date().toISOString(),
        }),
      }).catch(() => {});
    } catch {}
  }, [course.id, course.title]);

  const handleEnroll = async () => {
    if (course.isFree) {
      setIsEnrolled(true);
      await addId(ENROLLED_KEY, course.id);
      recordEnrollment();
      Alert.alert('Enrolled Successfully', `You are now enrolled in "${course.title}"`);
    } else {
      const priceNum = Number(course.price);
      if (!priceNum || priceNum <= 0) {
        Alert.alert('Price unavailable', 'We could not determine the price of this course. Please go back and try again.');
        return;
      }
      const scenario = {
        id:            `live_${course.id}`,
        type:          'live_course',
        category:      'learning',
        name:          course.title,
        description:   `Enrollment in live course "${course.title}"`,
        amountInPence: Math.round(priceNum * 100),
        currency:      'GBP',
        metadata: { course_id: course.id, course_title: course.title, instructor: course.instructor },
        successRoute:  'LiveCourseDetails',
        successParams: { course, justEnrolled: true },
        successMessage: `You are now enrolled in "${course.title}".`,
      };
      navigation.navigate('PaymentScreen', { scenario });
    }
  };

  const handleJoinLive = () => {
    if (course.status === 'Live Now') {
      Alert.alert('Join Live Session', `Join "${course.title}" now?`, [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Join',
          onPress: () => {
            if (course.liveUrl) {
              Linking.openURL(course.liveUrl).catch(() => Alert.alert('Error', 'Could not open the live session link.'));
            } else {
              navigation.navigate('LiveSession', { course });
            }
          },
        },
      ]);
    } else if (course.status === 'Upcoming') {
      handleReminder();
    } else {
      navigation.navigate('SessionRecording', { course });
    }
  };

  const handleReminder = async () => {
    if (isReminderSet) {
      await removeId(REMINDER_KEY, course.id);
      setIsReminderSet(false);
      Alert.alert('Reminder Removed', `Reminder for "${course.title}" has been removed.`);
    } else {
      await addId(REMINDER_KEY, course.id);
      setIsReminderSet(true);
      Alert.alert('Reminder Set', `Reminder set for ${fmtDate(course.date)} at ${course.time || 'TBA'}`);
    }
  };

  const handleShare = async () => {
    try {
      await Share.share({
        message: `Check out this live course: "${course.title}" by ${course.instructor}. ${course.subtitle}`,
        url: course.liveUrl || 'https://jobeng.ai/live-courses',
      });
    } catch (error) {
      Alert.alert('Error', 'Could not share the course.');
    }
  };

  const toggleFavorite = async () => {
    const next = !isFavorite;
    setIsFavorite(next);
    if (next) await addId(FAVORITE_KEY, course.id);
    else await removeId(FAVORITE_KEY, course.id);
  };

  const topics = Array.isArray(course.topics) ? course.topics : [];
  const requirements = Array.isArray(course.requirements) ? course.requirements : [];

  const TABS = [
    { id: 'overview', label: 'Overview', icon: 'information-outline' },
    { id: 'curriculum', label: 'Curriculum', icon: 'book-open-variant' },
    { id: 'instructor', label: 'Instructor', icon: 'account-tie' },
  ];

  const renderOverview = () => (
    <>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📝 Description</Text>
        <Text style={styles.description}>{course.description || 'No description provided.'}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📅 Session Details</Text>
        <View style={styles.detailsCard}>
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="calendar" size={20} color="#0e4e96" />
            <Text style={styles.detailLabel}>Date:</Text>
            <Text style={styles.detailValue}>{fmtDate(course.date)}</Text>
          </View>
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="clock-outline" size={20} color="#0e4e96" />
            <Text style={styles.detailLabel}>Time:</Text>
            <Text style={styles.detailValue}>{course.time || 'TBA'}</Text>
          </View>
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="web" size={20} color="#0e4e96" />
            <Text style={styles.detailLabel}>Platform:</Text>
            <Text style={styles.detailValue}>{hostnameOf(course.liveUrl)}</Text>
          </View>
        </View>
      </View>

      {course.recordingAvailable && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>🎥 Recording Available</Text>
          <View style={styles.recordingCard}>
            <MaterialCommunityIcons name="video" size={28} color="#4CAF50" />
            <View style={styles.recordingInfo}>
              <Text style={styles.recordingTitle}>Session Recording</Text>
              <Text style={styles.recordingDesc}>
                {course.status === 'Past'
                  ? 'This session has ended. Watch the recording anytime.'
                  : 'Recording will be available after the live session ends.'}
              </Text>
            </View>
            <TouchableOpacity style={styles.watchButton} onPress={() => navigation.navigate('SessionRecording', { course })}>
              <Text style={styles.watchButtonText}>WATCH</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </>
  );

  const renderCurriculum = () => (
    <>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📚 Topics Covered</Text>
        {topics.length ? (
          <View style={styles.topicsContainer}>
            {topics.map((topic, index) => (
              <View key={index} style={styles.topicChip}>
                <MaterialCommunityIcons name="check-circle" size={16} color="#4CAF50" />
                <Text style={styles.topicText}>{topic}</Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.description}>Curriculum details will be announced soon.</Text>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>⚙️ Requirements</Text>
        {requirements.length ? (
          <View style={styles.requirementsContainer}>
            {requirements.map((req, index) => (
              <View key={index} style={styles.requirementItem}>
                <MaterialCommunityIcons name="checkbox-marked-circle-outline" size={18} color="#0e4e96" />
                <Text style={styles.requirementText}>{req}</Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.description}>No special requirements.</Text>
        )}
      </View>
    </>
  );

  const renderInstructor = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>👤 Your Instructor</Text>
      <View style={styles.instructorCard}>
        <View style={styles.instructorCardAvatar}>
          <MaterialCommunityIcons name="account" size={36} color="#0e4e96" />
        </View>
        <Text style={styles.instructorCardName}>{course.instructor}</Text>
        <Text style={styles.instructorCardTitle}>{course.instructorTitle}</Text>
        <View style={styles.instructorMetaRow}>
          <View style={styles.instructorMeta}>
            <MaterialCommunityIcons name="school" size={18} color="#64748B" />
            <Text style={styles.instructorMetaText}>{course.category}</Text>
          </View>
          <View style={styles.instructorMeta}>
            <MaterialCommunityIcons name="chart-bar" size={18} color="#64748B" />
            <Text style={styles.instructorMetaText}>{course.level}</Text>
          </View>
        </View>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor={course.color} />

      <LinearGradient colors={[course.color, `${course.color}DD`]} style={styles.header}>
        <View style={styles.headerTop}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={28} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity onPress={toggleFavorite} style={styles.favoriteButton}>
            <Ionicons name={isFavorite ? 'heart' : 'heart-outline'} size={28} color={isFavorite ? '#FF4081' : '#fff'} />
          </TouchableOpacity>
        </View>

        <View style={styles.courseHeaderContent}>
          <View style={styles.liveBadgeContainer}>
            <View style={[
              styles.liveBadge,
              course.status === 'Live Now' && styles.liveNowBadge,
              course.status === 'Upcoming' && styles.upcomingBadge,
              course.status === 'Past' && styles.pastBadge,
            ]}>
              <Text style={styles.liveBadgeText}>
                {course.status === 'Live Now' ? '🔴 LIVE NOW' : course.status === 'Upcoming' ? '⏰ UPCOMING' : '📼 RECORDED'}
              </Text>
            </View>
          </View>

          <Text style={styles.courseTitle} numberOfLines={2}>{course.title}</Text>
          <Text style={styles.courseSubtitle} numberOfLines={2}>{course.subtitle}</Text>

          <View style={styles.instructorRow}>
            <View style={styles.instructorAvatar}>
              <MaterialCommunityIcons name="account" size={22} color="#fff" />
            </View>
            <View style={styles.instructorInfo}>
              <Text style={styles.instructorName}>{course.instructor}</Text>
              <Text style={styles.instructorTitle}>{course.instructorTitle}</Text>
            </View>
          </View>
        </View>
      </LinearGradient>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <MaterialCommunityIcons name="account-group" size={20} color="#64748B" />
            <Text style={styles.statNumber}>{course.enrolled}</Text>
            <Text style={styles.statLabel}>Enrolled</Text>
          </View>
          <View style={styles.statItem}>
            <MaterialCommunityIcons name="timer" size={20} color="#64748B" />
            <Text style={styles.statNumber}>{course.duration || '—'}</Text>
            <Text style={styles.statLabel}>Duration</Text>
          </View>
          <View style={styles.statItem}>
            <MaterialCommunityIcons name="chart-bar" size={20} color="#64748B" />
            <Text style={styles.statNumber}>{course.level}</Text>
            <Text style={styles.statLabel}>Level</Text>
          </View>
          <View style={styles.statItem}>
            <MaterialCommunityIcons name={course.isFree ? 'tag-outline' : 'tag'} size={20} color="#64748B" />
            <Text style={styles.statNumber}>{course.isFree ? 'FREE' : `£${course.price}`}</Text>
            <Text style={styles.statLabel}>Price</Text>
          </View>
        </View>

        {/* Tab bar */}
        <View style={styles.tabBar}>
          {TABS.map(t => (
            <TouchableOpacity
              key={t.id}
              style={[styles.tabItem, activeTab === t.id && styles.tabItemActive]}
              onPress={() => setActiveTab(t.id)}
            >
              <MaterialCommunityIcons name={t.icon} size={18} color={activeTab === t.id ? '#0e4e96' : '#94a3b8'} />
              <Text style={[styles.tabLabel, activeTab === t.id && styles.tabLabelActive]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'curriculum' && renderCurriculum()}
        {activeTab === 'instructor' && renderInstructor()}

        <View style={{ height: 24 }} />
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity style={styles.shareButton} onPress={handleShare}>
          <Ionicons name="share-social-outline" size={24} color="#0e4e96" />
        </TouchableOpacity>

        {course.status === 'Live Now' ? (
          <TouchableOpacity style={[styles.enrollButton, { backgroundColor: '#ef4444' }]} onPress={handleJoinLive}>
            <Text style={styles.enrollButtonText}>🔴 JOIN LIVE NOW</Text>
          </TouchableOpacity>
        ) : course.status === 'Upcoming' ? (
          isEnrolled ? (
            <TouchableOpacity style={[styles.enrollButton, { backgroundColor: isReminderSet ? '#16a34a' : '#0e4e96' }]} onPress={handleReminder}>
              <Text style={styles.enrollButtonText}>{isReminderSet ? '🔔 REMINDER SET' : '🔔 SET REMINDER'}</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity style={styles.enrollButton} onPress={handleEnroll}>
              <Text style={styles.enrollButtonText}>{course.isFree ? 'ENROLL FOR FREE' : `ENROLL NOW — £${course.price}`}</Text>
            </TouchableOpacity>
          )
        ) : isEnrolled ? (
          <TouchableOpacity style={[styles.enrollButton, { backgroundColor: '#6b7280' }]} onPress={() => navigation.navigate('SessionRecording', { course })}>
            <Text style={styles.enrollButtonText}>📼 WATCH RECORDING</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={styles.enrollButton} onPress={handleEnroll}>
            <Text style={styles.enrollButtonText}>{course.isFree ? 'GET FREE ACCESS' : `BUY RECORDING — £${course.price}`}</Text>
          </TouchableOpacity>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#f0f4f8' },
  header: { paddingTop: 16, paddingBottom: 24, paddingHorizontal: 20 },
  headerTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  backButton: { padding: 4 },
  favoriteButton: { padding: 4 },
  courseHeaderContent: {},
  liveBadgeContainer: { flexDirection: 'row', marginBottom: 12 },
  liveBadge: { backgroundColor: 'rgba(255,255,255,0.25)', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 12 },
  liveNowBadge: { backgroundColor: '#ef4444' },
  upcomingBadge: { backgroundColor: '#f59e0b' },
  pastBadge: { backgroundColor: 'rgba(0,0,0,0.3)' },
  liveBadgeText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  courseTitle: { fontSize: 24, fontWeight: 'bold', color: '#fff', marginBottom: 6 },
  courseSubtitle: { fontSize: 15, color: '#fff', opacity: 0.9, marginBottom: 16 },
  instructorRow: { flexDirection: 'row', alignItems: 'center' },
  instructorAvatar: {
    width: 44, height: 44, borderRadius: 22, backgroundColor: 'rgba(255,255,255,0.25)',
    justifyContent: 'center', alignItems: 'center', marginRight: 12,
  },
  instructorInfo: {},
  instructorName: { fontSize: 16, fontWeight: '600', color: '#fff' },
  instructorTitle: { fontSize: 13, color: '#fff', opacity: 0.85 },
  content: { flex: 1 },
  statsRow: {
    flexDirection: 'row', backgroundColor: '#fff', marginHorizontal: 15, marginTop: -16,
    borderRadius: 14, padding: 16, justifyContent: 'space-between',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 6, elevation: 4,
  },
  statItem: { alignItems: 'center', flex: 1 },
  statNumber: { fontSize: 14, fontWeight: 'bold', color: '#1f2937', marginTop: 4 },
  statLabel: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  tabBar: { flexDirection: 'row', marginHorizontal: 15, marginTop: 16, backgroundColor: '#fff', borderRadius: 12, padding: 4 },
  tabItem: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderRadius: 8, gap: 4 },
  tabItemActive: { backgroundColor: '#e9eef4' },
  tabLabel: { fontSize: 13, color: '#94a3b8', fontWeight: '600', marginLeft: 4 },
  tabLabelActive: { color: '#0e4e96' },
  section: { backgroundColor: '#fff', marginHorizontal: 15, marginTop: 16, borderRadius: 14, padding: 18 },
  sectionTitle: { fontSize: 17, fontWeight: 'bold', color: '#1f2937', marginBottom: 12 },
  description: { fontSize: 15, color: '#4b5563', lineHeight: 22 },
  topicsContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  topicChip: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0fdf4',
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 20, marginRight: 8, marginBottom: 8,
  },
  topicText: { fontSize: 13, color: '#166534', marginLeft: 6, fontWeight: '500' },
  requirementsContainer: {},
  requirementItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  requirementText: { fontSize: 14, color: '#4b5563', marginLeft: 10 },
  detailsCard: { backgroundColor: '#f8fafc', borderRadius: 10, padding: 14 },
  detailRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  detailLabel: { fontSize: 14, color: '#64748B', marginLeft: 10, width: 70 },
  detailValue: { fontSize: 14, color: '#1f2937', fontWeight: '600', flex: 1 },
  recordingCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0fdf4', borderRadius: 12, padding: 16 },
  recordingInfo: { flex: 1, marginLeft: 12 },
  recordingTitle: { fontSize: 15, fontWeight: '600', color: '#1f2937' },
  recordingDesc: { fontSize: 13, color: '#64748B', marginTop: 2 },
  watchButton: { backgroundColor: '#4CAF50', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  watchButtonText: { color: '#fff', fontSize: 13, fontWeight: 'bold' },
  instructorCard: { alignItems: 'center', padding: 8 },
  instructorCardAvatar: {
    width: 72, height: 72, borderRadius: 36, backgroundColor: '#e9eef4',
    justifyContent: 'center', alignItems: 'center', marginBottom: 12,
  },
  instructorCardName: { fontSize: 18, fontWeight: 'bold', color: '#1f2937' },
  instructorCardTitle: { fontSize: 14, color: '#64748B', marginTop: 2, marginBottom: 16 },
  instructorMetaRow: { flexDirection: 'row', gap: 24 },
  instructorMeta: { flexDirection: 'row', alignItems: 'center', marginHorizontal: 8 },
  instructorMetaText: { fontSize: 13, color: '#64748B', marginLeft: 6 },
  footer: {
    flexDirection: 'row', alignItems: 'center', padding: 16, backgroundColor: '#fff',
    borderTopWidth: 1, borderTopColor: '#e5e7eb', gap: 12,
  },
  shareButton: {
    width: 52, height: 52, borderRadius: 12, borderWidth: 1, borderColor: '#e0e0e0',
    justifyContent: 'center', alignItems: 'center',
  },
  enrollButton: { flex: 1, backgroundColor: '#0e4e96', paddingVertical: 16, borderRadius: 12, alignItems: 'center' },
  enrollButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  enrolledButton: { flex: 1, backgroundColor: '#16a34a', paddingVertical: 16, borderRadius: 12, alignItems: 'center' },
  enrolledButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
});

export default LiveCourseDetailsScreen;
